import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Button from '../../../composants/Button/Button';
import CompteARebourd from '../../../composants/CompteARebourd/CompteARebourd';
import ValidationPopUp from '../../../composants/ValidationPopUp/ValidationPopUp';
import CGVAccept from '../../../composants/CGVAccept/CGVAccept';
import Banner from '../../../composants/Banner/Banner';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import OperationPreparationBC from './OperationPreparationBC';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import SignatureFlow from '../../../composants/Marche/SignatureFlow';
import { useAuth } from '../../../contexts/AuthContext';
import { isSignataireRole } from '../../../utils/roleUtils';
import { formatPrice, getStatusColor } from '../../../utils/entityUtils';
import { useLotImage } from '../../../utils/getLotImage';
import { StatusId, OperationBC, HistoriqueEvent, Signataire } from '../types';
import { computeOffreActions, operationActionsInternals } from '../domain/operationActions';
import { NextTabRender, hasOffreWorkflowAction } from '../domain/operationNextTab';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import { commandeDuRang } from '../domain/preparation';
import { reloadToTab } from '../../../utils/tabPreference';
import {
  formatOffreArticles,
  totalOffreArticles,
  computeOffreState,
  computeOffreStatusFlags,
  messageRefusAcheteur,
  offrePrestataireRecue,
} from '../domain/operationOffre';
import {
  accepterOffre,
  refuserOffre,
  annulerOperation,
  updateReferenceInterne,
  loadSignataires,
  notifierResponsable,
  fetchSignatureModeBdc,
} from '../services/operationBcService';
// Porte les classes .bcadm-desc* (grille 12-col + champs read-only) partagees avec la Consultation
// acheteur du MEME module : l'Offre technique reprend a l'identique la documentation technique.
import './DescriptifAdministratifBC.css';
import './OperationOffre.css';

const { isExpired: isOperationExpired } = operationActionsInternals;

interface OperationOffreProps {
  commande: OperationBC;
  rang: number;
  acceptCGV?: boolean;
  setAcceptCGV?: React.Dispatch<React.SetStateAction<boolean>>;
  printRef?: React.RefObject<HTMLElement>;
  // CTA de navigation vers l'onglet suivant, resolu par OperationContent. L'argument dit si CETTE
  // barre propose deja un orange qui fait avancer le workflow : c'est la feuille qui le sait
  // (role signataire, chrono expire), pas le routeur.
  nextTabFor?: (hasWorkflowAction: boolean) => NextTabRender;
}

interface SelectedResponsable {
  id: number;
  type: string;
}

// Onglet Offre acheteur-BC (V2) : decision sur l'offre du prestataire d'un rang.
// Autonome : aucune dependance a l'ancienne feuille composants/OperationOffreBC.
// Logique des boutons -> domain/operationActions ; derivations -> domain/operationOffre ;
// appels API -> services/operationBcService.
const OperationOffre: React.FC<OperationOffreProps> = ({
  commande,
  rang,
  acceptCGV = false,
  setAcceptCGV = () => { },
  printRef,
  nextTabFor,
}) => {
  const { user } = useAuth();
  const isSignataire = isSignataireRole(user?.role?.toString() ?? '');
  const lotImage = useLotImage(
    commande.operation?.lot_technique?.nom || commande.lot_technique?.nom,
  );

  const [operationData, setOperationData] = useState<OperationBC>(commande);

  // Etats UI
  const [loading, setLoading] = useState(false);
  // Feedback inline (remplace les alert()) : succes/erreur affiche en banniere en tete.
  const [feedback, setFeedback] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCGVModalOpen, setIsCGVModalOpen] = useState(false);
  const [readyToAcceptOffreAfterCGV, setReadyToAcceptOffreAfterCGV] = useState(false);
  const [isAcceptOffreModalOpen, setIsAcceptOffreModalOpen] = useState(false);
  const [isRefuseOffreModalOpen, setIsRefuseOffreModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // Notification au responsable signataire
  const [isNotifyResponsableModalOpen, setIsNotifyResponsableModalOpen] = useState(false);
  const [responsables, setResponsables] = useState<Signataire[]>([]);
  const [selectedResponsable, setSelectedResponsable] = useState<SelectedResponsable | null>(null);
  const [responsablesLoading, setResponsablesLoading] = useState(false);

  // Reference interne (panier inverse)
  const [referenceInterne, setReferenceInterne] = useState('');
  const [originalReference, setOriginalReference] = useState('');
  const [referenceInitialized, setReferenceInitialized] = useState(false);
  const [updatingReference, setUpdatingReference] = useState(false);

  // Mode de signature BDC + flow manuscrit
  const [bdcSignatureMode, setBdcSignatureMode] = useState<'automatique' | 'manuscrite'>(() => {
    const cached = localStorage.getItem('bdc_signature_mode');
    return cached === 'manuscrite' || cached === 'automatique' ? cached : 'automatique';
  });
  const [showBdcSignatureModal, setShowBdcSignatureModal] = useState(false);
  const [pendingBdcSignData, setPendingBdcSignData] = useState<{ operationId: number; commandeId: number } | null>(null);

  // Expiration : l'event runtime `chronoExpired` est local, le reste (statut/date)
  // est calcule par le domaine.
  const [chronoExpired, setChronoExpired] = useState(false);

  // La donnee vient de la prop parent (rafraichie). Pas de fetch ni de sniffing d'URL.
  useEffect(() => {
    setOperationData(commande);
  }, [commande]);

  useEffect(() => {
    const handler = () => setChronoExpired(true);
    window.addEventListener('chronoExpired', handler);
    return () => window.removeEventListener('chronoExpired', handler);
  }, []);

  useEffect(() => {
    fetchSignatureModeBdc()
      .then((mode) => {
        if (mode) {
          localStorage.setItem('bdc_signature_mode', mode);
          setBdcSignatureMode(mode);
        }
      })
      .catch(() => { /* garder le mode cache ou le defaut */ });
  }, []);

  useEffect(() => {
    if (!referenceInitialized) {
      const existing =
        operationData.reference || operationData.formulaire?.referenceInterne || '';
      setReferenceInterne(existing);
      setOriginalReference(existing);
      setReferenceInitialized(true);
    }
  }, [operationData, referenceInitialized]);

  const expired = useMemo(
    () => isOperationExpired(operationData, chronoExpired),
    [operationData, chronoExpired],
  );

  // Offre du prestataire pas encore recue -> les deux sections qui la presentent sont VERROUILLEES.
  // Elles affichaient sinon un tiret et un tableau vide, indistinguables d'une offre deposee vide.
  const offreRecue = useMemo(
    () => offrePrestataireRecue(operationData, rang),
    [operationData, rang],
  );
  const verrouOffre = "En attente de la réponse du titulaire";

  const notificationInfo = useMemo(() => {
    const operationId = operationData.id;
    const historique: HistoriqueEvent[] = operationData.historique || [];
    if (!operationId) return { sent: false, labels: [] as string[] };

    const events = historique.filter(
      (event) => event.prefix === 'Notification' && event.type === `BDC.Operation.${operationId}`,
    );
    if (events.length > 0) {
      const labels = events
        .slice()
        .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime())
        .map((event) => event.label)
        .filter((label): label is string => label !== null && label !== undefined);
      return { sent: true, labels };
    }
    return { sent: false, labels: [] as string[] };
  }, [operationData]);

  // Commande du rang consulte : elle porte le titulaire de ce rang ET l'offre technique qu'il a
  // saisie. On la resout une fois pour le recap de preparation et pour l'affichage de l'offre.
  const commandeRang = useMemo(() => commandeDuRang(operationData, rang), [operationData, rang]);

  const articles = useMemo(() => formatOffreArticles(operationData, rang), [operationData, rang]);
  const totalOffre = useMemo(() => totalOffreArticles(articles), [articles]);
  const offreState = useMemo(() => computeOffreState(operationData, rang), [operationData, rang]);
  const statusFlags = useMemo(() => computeOffreStatusFlags(operationData, rang), [operationData, rang]);
  const actions = useMemo(
    () => computeOffreActions(operationData, rang, { isSignataire, chronoExpired }),
    [operationData, rang, isSignataire, chronoExpired],
  );

  // --- Handlers ---------------------------------------------------------------
  const findCommandeId = useCallback(
    (): number | undefined =>
      operationData.commandes?.find((c) => parseInt(String(c.rang), 10) === rang)?.id,
    [operationData, rang],
  );

  const saveReferenceIfChanged = useCallback(async (): Promise<boolean> => {
    if (referenceInterne === originalReference) return true;
    try {
      setUpdatingReference(true);
      const result = await updateReferenceInterne(operationData.id, referenceInterne.trim());
      if (result?.data) {
        const newRef = referenceInterne.trim();
        setOriginalReference(newRef);
        setOperationData((prev) => ({
          ...prev,
          reference: newRef,
          formulaire: { ...prev.formulaire, referenceInterne: newRef },
        }));
      }
      return true;
    } catch (error) {
      setFeedback({ type: 'danger', message: error instanceof Error ? error.message : 'Erreur lors de la mise à jour de la référence' });
      return false;
    } finally {
      setUpdatingReference(false);
    }
  }, [referenceInterne, originalReference, operationData]);

  // Apres acquisition des CGV : signature (manuscrite) ou popup de confirmation (automatique).
  const openSignatureOrAccept = useCallback(() => {
    if (bdcSignatureMode === 'manuscrite') {
      const commandeId = findCommandeId();
      if (operationData.id && commandeId) {
        setPendingBdcSignData({ operationId: operationData.id, commandeId });
        setShowBdcSignatureModal(true);
      }
    } else {
      setIsAcceptOffreModalOpen(true);
    }
  }, [bdcSignatureMode, findCommandeId, operationData]);

  const handleAccepterOffreAvecCGV = useCallback(async () => {
    const ok = await saveReferenceIfChanged();
    if (!ok) return;
    // Les CGV passent TOUJOURS avant la signature. On ne saute la popup que si elles
    // ont deja ete cochees dans cette session (un refresh les redemande donc).
    if (acceptCGV) {
      openSignatureOrAccept();
    } else {
      setReadyToAcceptOffreAfterCGV(true);
      setIsCGVModalOpen(true);
    }
  }, [saveReferenceIfChanged, acceptCGV, openSignatureOrAccept]);

  const handleAcceptOffre = useCallback(async () => {
    try {
      setLoading(true);
      const commandeId = findCommandeId();
      if (!operationData.id) throw new Error("ID de l'opération non trouvé");
      if (!commandeId) throw new Error(`ID de la commande du rang ${rang} non trouvé`);
      await accepterOffre(operationData.id, commandeId);
      setFeedback({ type: 'success', message: 'Offre acceptée avec succès !' });
      window.location.reload();
    } catch (error) {
      setFeedback({ type: 'danger', message: error instanceof Error ? error.message : "Erreur lors de l'acceptation" });
    } finally {
      setLoading(false);
    }
  }, [findCommandeId, operationData, rang]);

  const handleRefuseOffre = useCallback(async () => {
    try {
      setLoading(true);
      const commandeId = findCommandeId();
      if (!operationData.id) throw new Error("ID de l'opération non trouvé");
      if (!commandeId) throw new Error(`ID de la commande du rang ${rang} non trouvé`);
      await refuserOffre(operationData.id, commandeId);
      setFeedback({ type: 'danger', message: 'Offre refusée.' });
      window.location.reload();
    } catch (error) {
      setFeedback({ type: 'danger', message: error instanceof Error ? error.message : 'Erreur lors du refus' });
    } finally {
      setLoading(false);
    }
  }, [findCommandeId, operationData, rang]);

  const handleCancelOperation = useCallback(async () => {
    setIsDeleting(true);
    try {
      await annulerOperation(operationData.id);
      window.location.href = '/operations';
    } catch (error) {
      setFeedback({ type: 'danger', message: error instanceof Error ? error.message : "Erreur lors de l'annulation de l'opération." });
    } finally {
      setIsDeleting(false);
      setIsCancelModalOpen(false);
    }
  }, [operationData]);

  const handleNotifyResponsable = useCallback(async () => {
    const ok = await saveReferenceIfChanged();
    if (!ok) return;
    setIsNotifyResponsableModalOpen(true);
    setResponsablesLoading(true);
    try {
      const list = await loadSignataires();
      setResponsables(list);
    } catch {
      setResponsables([]);
    } finally {
      setResponsablesLoading(false);
    }
  }, [saveReferenceIfChanged]);

  const handleConfirmNotifyResponsable = useCallback(async () => {
    if (!selectedResponsable) {
      setFeedback({ type: 'danger', message: 'Veuillez sélectionner un responsable' });
      return;
    }
    try {
      const result = await notifierResponsable(
        operationData.id,
        selectedResponsable.id,
        selectedResponsable.type,
      );
      setFeedback({ type: 'success', message: `Demande envoyée à ${result.responsable?.prenom} ${result.responsable?.nom} avec succès !` });
      setIsNotifyResponsableModalOpen(false);
      setSelectedResponsable(null);
    } catch (error) {
      setFeedback({ type: 'danger', message: `Erreur lors de l'envoi de la demande au responsable: ${(error as Error).message}` });
    }
  }, [selectedResponsable, operationData]);

  // --- Helpers d'affichage ----------------------------------------------------
  const getDisplayStatus = (): string => operationData.status?.nom || 'Statut inconnu';
  const getSousTitre = (): string =>
    '';
  const getSubtitle = (): string => {
    const lotTechnique = operationData.operation?.lot_technique || operationData.lot_technique;
    const departement =
      operationData.operation?.lot_departement?.departement || operationData.lot_departement?.departement;
    if (lotTechnique && departement) {
      return `${lotTechnique.id} - ${lotTechnique.nom}, ${departement.id} - ${departement.nom}`;
    }
    return 'Informations de lot non disponibles';
  };

  // Les oranges de CETTE barre qui font avancer le workflow. Tant que l'un d'eux est propose,
  // c'est lui le CTA de l'onglet : le domaine masque alors la navigation.
  const nextTab = nextTabFor?.(hasOffreWorkflowAction(actions));

  const showActionRow =
    actions.annuler.visible ||
    actions.refuserOffre.visible ||
    actions.accepterOffre.visible ||
    actions.demanderValidation.visible ||
    // Sans ca, l'offre acceptee (aucune action due) n'afficherait aucune barre, donc aucune
    // navigation vers le bon de commande signe : c'est pourtant la suite du parcours.
    Boolean(nextTab?.visible);

  // Bannieres d'ETAT (refus / expiration / annulation / acceptation), definies UNE fois et rendues en
  // HAUT ET EN BAS de l'onglet — meme dispositif que "Notification et signature" : l'onglet est long,
  // l'etat doit se lire sans avoir a remonter. Deux copies de JSX finiraient par diverger.
  const stateBanners = (
    <>
      {offreState.showRefusMessage && offreState.refusInfo && (
        // `danger` : un refus, une expiration ou une annulation ferment le parcours de ce rang.
        // `warning` les rangeait au meme niveau qu'un simple rappel d'echeance.
        <Banner variant="danger" filled message={messageRefusAcheteur(offreState.refusInfo)} />
      )}
      {statusFlags.isAccepted && (
        <Banner
          variant="success"
          filled
          message={(() => {
            const d = operationData.updated_at ? new Date(operationData.updated_at) : null;
            const dateStr =
              d && !Number.isNaN(d.getTime())
                ? ` le ${d.toLocaleDateString('fr-FR')} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
                : '';
            // `isAccepted` == statut Valide(7) : les deux signatures sont posees a ce stade.
            return `Bon de commande accepté et signé${dateStr}`;
          })()}
        />
      )}
    </>
  );

  return (
    <>
      <div className="consultation-bc">
        {feedback && <Banner variant={feedback.type} message={feedback.message} />}
        {stateBanners}
        {expired && (
          <div
            className="expired-notice"
            style={{
              backgroundColor: '#fee',
              border: '2px solid #c33',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              color: '#c33',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            {operationData.status?.id === StatusId.Annule
              ? "Cette commande a été annulée par l'acheteur. Vous ne pouvez plus effectuer d'actions."
              : 'Cette commande est expirée. Vous ne pouvez plus modifier votre offre ni effectuer d\'actions.'}
          </div>
        )}

        {/* Preparation du BC, EN TETE de l'onglet. Elle porte le rang de CET onglet : chaque "Offre
            Rang N" affiche donc le titulaire qui lui correspond, et non un titulaire unique — c'est la
            difference de fond avec le MS, qui n'en a qu'un. */}
        <OperationPreparationBC operation={operationData} titulaire={commandeRang} />

        {/* Offre technique : l'acheteur consulte celle REDIGEE par le prestataire de ce rang, en
            lecture seule. Le descriptif technique de l'acheteur (URL / modalite / liste / cahier des
            charges) n'est plus repris ici — il vit dans l'onglet Consultation, ou l'acheteur l'a saisi.
            `.bcadm-besoin-readonly` preserve les retours a la ligne (white-space: pre-wrap) : la
            saisie presta est un texte multi-ligne libre. */}
        <SectionPliable
          title="Offre technique"
          defaultOpen
          locked={!offreRecue}
          lockedHint={verrouOffre}
        >
          <Banner
            variant="info"
            message="L'offre technique ci-dessous a été rédigée par le prestataire."
          />
          <div className="section-content">
            <div className="form-group">
              <label>Offre technique du prestataire (rang {rang})</label>
              <div className="input-inactif bcadm-besoin-readonly">
                {commandeRang?.offre_technique || '-'}
              </div>
            </div>
          </div>
        </SectionPliable>

        <SectionPliable
          title="Offre financière"
          defaultOpen
          locked={!offreRecue}
          lockedHint={verrouOffre}
        >
          <div className="section-content">
            {articles.length > 0 ? (
              <div className="articles-table-container">
                <div className="articles-table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>N° Réf</th>
                        <th>Description de la prestation</th>
                        <th>Unité</th>
                        <th>Quantité</th>
                        <th>Prix unitaire HT</th>
                        <th>Total HT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {articles.map((article, index) => (
                        <tr key={article.id || index}>
                          <td>{article.code}</td>
                          <td>
                            <div className="article-name">{article.nom}</div>
                          </td>
                          <td>{article.unite}</td>
                          <td>{article.quantite}</td>
                          <td>{formatPrice(article.prixUnitaire, 4, true)}</td>
                          <td>{formatPrice(article.totalHT, 4, true)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="articles-actions">
                  <div className="total-section">
                    <p>
                      <strong>
                        Total de l'offre (Rang {rang}) : {formatPrice(totalOffre, 4, true)} HT
                      </strong>
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-articles-message">
                <p>Aucun article défini pour cette offre.</p>
              </div>
            )}
          </div>
        </SectionPliable>

        {actions.modifierReference.visible && (
          <SectionPliable title="Numéro de référence interne" defaultOpen>
            <div className="section-content">
              <Banner
                variant="info"
                message="Vous pouvez renseigner un numéro de référence interne (ex: numéro d'engagement comptable) avant d'accepter l'offre. Ce champ est facultatif."
                className="reference-interne-section__info"
              />
              <div className="form-group" style={{ maxWidth: '400px', marginTop: '12px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
                  N° de référence interne (facultatif)
                </label>
                <input
                  type="text"
                  value={referenceInterne}
                  onChange={(e) => setReferenceInterne(e.target.value)}
                  placeholder="Exemple : numéro d'engagement comptable"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                  disabled={updatingReference}
                />
              </div>
            </div>
          </SectionPliable>
        )}

        {/* Le bon de commande signe (preview + telechargement) et le mail de notification vivent
            desormais dans l'onglet "Notification et signature" (actif au statut Valide=7). */}

        {/* Banniere de refus/expiration affichee EN HAUT (voir apres <div consultation-bc>). */}

        {/* {!operationData.transferred && statusFlags.isPending && (
          <div className="section-content consultation-info">
            <div className="info-header">
              <h4>En attente de l'acceptation du prestataire</h4>
              <p>Le prestataire de rang {rang} doit accepter ou refuser cette opération.</p>
              {operationData.expire_at && (
                <p>
                  <strong>Échéance dans :</strong> <CompteARebourd targetDate={operationData.expire_at} />
                </p>
              )}
            </div>
          </div>
        )} */}

        {/* Rappel de l'etat juste avant la barre d'actions : l'onglet est long, et c'est ici que se
            prend la decision. Meme JSX qu'en tete (defini une fois), pas une recopie. */}
        {stateBanners}

        <div className="action-buttons-wrapper">
          {showActionRow && (
            <>
              <div className="action-buttons-row">
                {actions.annuler.visible && (
                  <Button
                    value={isDeleting ? 'Annulation...' : 'Annuler la commande'}
                    variant="cancel"
                    onClick={() => setIsCancelModalOpen(true)}
                    disabled={actions.annuler.disabled || isDeleting}
                  />
                )}
                {actions.refuserOffre.visible && (
                  <Button
                    value="Refuser l'offre"
                    variant="secondary"
                    onClick={() => setIsRefuseOffreModalOpen(true)}
                    disabled={actions.refuserOffre.disabled || loading}
                  />
                )}
                {actions.accepterOffre.visible && (
                  <Button
                    value="Accepter l'offre"
                    variant="primary"
                    onClick={handleAccepterOffreAvecCGV}
                    disabled={actions.accepterOffre.disabled || loading}
                  />
                )}
                {actions.demanderValidation.visible && (
                  <Button
                    value="Obtenir la validation de l'acheteur signataire"
                    variant="primary"
                    onClick={handleNotifyResponsable}
                  />
                )}
                {nextTab && (
                  <NextTabButton
                    label={nextTab.label}
                    hidden={!nextTab.visible}
                    // `no-print` : de la navigation n'a rien a faire sur un document imprime, et le
                    // ref d'impression de « Imprimer l'offre » couvre tout l'onglet. La classe est
                    // effective via la feuille @media print injectee par PrintButton.
                    className="no-print"
                    onClick={nextTab.onClick}
                  />
                )}
              </div>
              {actions.demanderValidation.visible &&
                notificationInfo.sent &&
                notificationInfo.labels.length > 0 && (
                  <div className="notifications-sent-list">
                    {notificationInfo.labels.map((label, index) => (
                      <div key={index} className="notification-sent-info">
                        <span className="notification-sent-icon">✓</span>
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                )}
            </>
          )}

          {/* Acceptation : affichee via le composant Banner en haut, plus de badge « ✓ Offre acceptée ». */}
          {/* Refus : affiche via le composant Banner (voir plus haut), plus de badge rouge. */}
          {statusFlags.isExpired && <div className="status-message expired">⏰ Offre expirée</div>}

          {printRef && (
            <div className="print-button-inline">
              <PrintButton
                targetRef={printRef}
                fileName={`${operationData.numero || operationData.operation?.numero} - Offre`}
                value="Imprimer l'offre"
                suiviHeaderProps={{
                  titre: operationData.operation?.nom || operationData.nom || '',
                  sousTitre: getSousTitre(),
                  subtitle: getSubtitle(),
                  status: getDisplayStatus(),
                  statusColor: getStatusColor(getDisplayStatus()),
                  delais: operationData.expire_at,
                  backgroundImage: lotImage,
                }}
              />
            </div>
          )}
        </div>

        {/* Modales */}
        <CGVAccept
          acceptCGV={acceptCGV}
          setAcceptCGV={setAcceptCGV}
          isOpen={isCGVModalOpen}
          onClose={() => {
            setIsCGVModalOpen(false);
            setReadyToAcceptOffreAfterCGV(false);
          }}
          onConfirm={() => {
            setAcceptCGV(true);
            setTimeout(() => {
              setIsCGVModalOpen(false);
              if (readyToAcceptOffreAfterCGV) {
                setReadyToAcceptOffreAfterCGV(false);
                openSignatureOrAccept();
              }
            }, 100);
          }}
        />

        <ValidationPopUp
          isOpen={isAcceptOffreModalOpen}
          title="Accepter l'offre"
          text={`Êtes-vous sûr de vouloir accepter cette offre pour un montant de ${formatPrice(
            totalOffre,
            4,
            true,
          )} HT ? Cette action est définitive.`}
          onClose={() => setIsAcceptOffreModalOpen(false)}
          onConfirm={async () => {
            setIsAcceptOffreModalOpen(false);
            await handleAcceptOffre();
          }}
          cancelLabel="Annuler"
          confirmLabel="Accepter l'offre"
        />

        <ValidationPopUp
          isOpen={isRefuseOffreModalOpen}
          title="Refuser l'offre"
          text="Êtes-vous sûr de vouloir refuser cette offre ?"
          onClose={() => setIsRefuseOffreModalOpen(false)}
          onConfirm={async () => {
            setIsRefuseOffreModalOpen(false);
            await handleRefuseOffre();
          }}
          cancelLabel="Annuler"
          confirmLabel="Refuser l'offre"
        />

        <ValidationPopUp
          isOpen={isCancelModalOpen}
          title="Annulation de la commande"
          text="Êtes-vous sûr de vouloir annuler cette commande ? L'opération et les commandes liées passeront en statut annulé. Cette action est irréversible."
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={handleCancelOperation}
          cancelLabel="Non"
          confirmLabel="Oui, annuler"
        />

        <ValidationPopUp
          isOpen={isNotifyResponsableModalOpen}
          title="Obtenir la validation de l'acheteur signataire"
          text=""
          onClose={() => {
            setIsNotifyResponsableModalOpen(false);
            setSelectedResponsable(null);
          }}
          onConfirm={handleConfirmNotifyResponsable}
          cancelLabel="Annuler"
          confirmLabel="Envoyer la demande"
        >
          <div className="mt-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionnez le responsable à qui envoyer la demande :
            </label>
            {responsablesLoading ? (
              <p className="text-gray-500">Chargement...</p>
            ) : responsables.length === 0 ? (
              <p className="text-red-500">Aucun responsable disponible</p>
            ) : (
              <select
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={selectedResponsable ? `${selectedResponsable.type}-${selectedResponsable.id}` : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const [type, id] = value.split('-');
                    setSelectedResponsable({ id: parseInt(id, 10), type });
                  } else {
                    setSelectedResponsable(null);
                  }
                }}
              >
                <option value="">-- Choisir un responsable --</option>
                {responsables.map((resp) => (
                  <option key={`${resp.type}-${resp.id}`} value={`${resp.type}-${resp.id}`}>
                    {resp.prenom} {resp.nom} -{' '}
                    {resp.type === 'responsable_achat' ? 'Acheteur signataire' : 'Validateur Achat'}
                    {resp.fonction ? ` (${resp.fonction})` : ''}
                  </option>
                ))}
              </select>
            )}
            {selectedResponsable && (
              <p className="mt-2 text-sm text-gray-600">
                Email :{' '}
                {
                  responsables.find(
                    (r) => r.id === selectedResponsable.id && r.type === selectedResponsable.type,
                  )?.email_contact
                }
              </p>
            )}
          </div>
        </ValidationPopUp>
      </div>

      {showBdcSignatureModal && pendingBdcSignData && (
        <SignatureFlow
          operationId={pendingBdcSignData.operationId}
          commandeId={pendingBdcSignData.commandeId}
          resourcePrefix="bdc"
          // Meme prefill que le lancement de consultation : le signataire designe dans le descriptif
          // administratif est propose par defaut, et reste modifiable dans la modale.
          initialNomComplet={
            operationData.signataire_commande || operationData.formulaire?.signataire
          }
          onClose={() => {
            setShowBdcSignatureModal(false);
            setPendingBdcSignData(null);
          }}
          onSigned={() => {
            setShowBdcSignatureModal(false);
            setPendingBdcSignData(null);
            // Liste de preferences : la signature acheteur ne finalise pas toujours la validation
            // (BC -> 7, cf. MsSignatureController). Si elle l'a fait, "Notification et signature" est
            // ouvert et on y va ; sinon on retombe sur l'Offre de CE rang. Le choix se fait au render
            // suivant, sur l'etat frais — pas ici, ou le statut est encore celui d'avant signature.
            reloadToTab(['notification', `offre-${rang}`, `consultation-${rang}`]);
          }}
        />
      )}
    </>
  );
};

export default OperationOffre;
